import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    DatePicker,
    Descriptions,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Radio,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/config';
import { entityOptions } from '../finans/api/financeApi';
import { formatNumberTR, inputNumberMoneyProps } from '../../utils/currency';

const { Text } = Typography;

// App convention: Bearer token from localStorage 'auth_token' (see AuthContext.getAuthHeaders)
const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
});

const fmt = (v) => formatNumberTR(v || 0);

// '12,13,20-25' -> QR adedi (backend parse_qr_araligi ile birebir)
const isDec = (x) => /^\d+$/.test(x);
const parseQrCount = (s) => {
    const set = new Set();
    String(s || '').replace(/;/g, ',').split(',').forEach((p) => {
        p = p.trim();
        if (!p) return;
        if (p.includes('-')) {
            const seg = p.split('-');
            if (seg.length === 2 && isDec(seg[0].trim()) && isDec(seg[1].trim())) {
                let lo = parseInt(seg[0], 10), hi = parseInt(seg[1], 10);
                if (lo > hi) { const t = lo; lo = hi; hi = t; }
                if (hi - lo <= 2000) for (let i = lo; i <= hi; i += 1) set.add(i);
            }
        } else if (isDec(p)) {
            set.add(parseInt(p, 10));
        }
    });
    return set.size;
};

const DepoKabinGirisiTab = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lookups, setLookups] = useState({ ureticiler: [], depolar: [] });

    // Finance lookups (cari/firma) + tevkifat — loaded lazily for the hakediş modal
    const [finLookups, setFinLookups] = useState(null);
    const [tevkifatlar, setTevkifatlar] = useState([]);

    // Create/Edit modal
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const qrInputRef = useRef(null);

    // Hakediş modal
    const [hakedisOpen, setHakedisOpen] = useState(false);
    const [hakedisRecord, setHakedisRecord] = useState(null);
    const [hakedisSaving, setHakedisSaving] = useState(false);
    const [hakedisForm] = Form.useForm();

    const wKdvOrani = Form.useWatch('kdv_orani', hakedisForm);
    const wKdvDahil = Form.useWatch('kdv_dahil', hakedisForm);
    const wTevkifat = Form.useWatch('tevkifat_kodu', hakedisForm);

    // QR aralığı -> miktar otomatik
    const wQr = Form.useWatch('qr', form);
    const qrCount = useMemo(() => parseQrCount(wQr), [wQr]);
    useEffect(() => {
        if (qrCount > 0) form.setFieldsValue({ miktar: qrCount });
    }, [qrCount, form]);

    const loadRows = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/depo-giris`, { headers: authHeaders() });
            setRows(res?.data?.data || []);
        } catch (e) {
            message.error(e?.response?.data?.detail || 'Kabin girişleri yüklenemedi');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadLookups = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/depo-giris/lookups`, { headers: authHeaders() });
            setLookups(res?.data?.data || { ureticiler: [], depolar: [] });
        } catch (e) {
            message.error(e?.response?.data?.detail || 'Üretici/depo listesi yüklenemedi');
        }
    }, []);

    // Reuses the finance lookups endpoint that powers entityOptions(lookups,'cari'|'firma')
    const loadFinanceData = useCallback(async () => {
        try {
            const [lk, tv] = await Promise.all([
                axios.get(`${API_BASE_URL}/finance/lookups`, { headers: authHeaders() }),
                axios.get(`${API_BASE_URL}/finance/tevkifat-kodlari`, { headers: authHeaders() }),
            ]);
            setFinLookups(lk?.data?.data || {});
            setTevkifatlar(tv?.data?.data || []);
        } catch (e) {
            message.error(e?.response?.data?.detail || 'Cari/firma listesi yüklenemedi');
        }
    }, []);

    useEffect(() => {
        loadRows();
        loadLookups();
    }, [loadRows, loadLookups]);

    const depoOptions = useMemo(
        () => (lookups.depolar || []).map((d) => ({ value: d.id, label: d.ad })),
        [lookups.depolar]
    );
    const ureticiOptions = useMemo(
        () => (lookups.ureticiler || []).map((u) => ({ value: u.id, label: u.ad })),
        [lookups.ureticiler]
    );
    const cariOptions = useMemo(() => entityOptions(finLookups || {}, 'cari'), [finLookups]);
    const firmaOptions = useMemo(() => entityOptions(finLookups || {}, 'firma'), [finLookups]);
    const tevkifatOptions = useMemo(
        () => (tevkifatlar || []).map((t) => ({ value: t.kod, label: t.ad })),
        [tevkifatlar]
    );

    // ---- Create / Edit ----
    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({ tarih: dayjs(), miktar: 1 });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditing(record);
        form.resetFields();
        form.setFieldsValue({
            tarih: record.tarih ? dayjs(record.tarih) : dayjs(),
            depo_lokasyon: record.depo_lokasyon,
            uretici: record.uretici,
            qr: record.qr,
            kabin_tipi: record.kabin_tipi,
            miktar: record.miktar,
            birim_fiyat: record.birim_fiyat,
            aciklama: record.aciklama,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        const body = {
            tarih: values.tarih ? values.tarih.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
            depo_lokasyon: values.depo_lokasyon,
            uretici: values.uretici,
            qr: values.qr || '',
            kabin_tipi: values.kabin_tipi || '',
            miktar: values.miktar,
            birim_fiyat: values.birim_fiyat,
            aciklama: values.aciklama || '',
        };
        setSaving(true);
        try {
            if (editing) {
                await axios.put(`${API_BASE_URL}/depo-giris/${editing.id}`, body, { headers: authHeaders() });
                message.success('Kabin girişi güncellendi');
            } else {
                await axios.post(`${API_BASE_URL}/depo-giris`, body, { headers: authHeaders() });
                message.success('Kabin girişi kaydedildi');
            }
            setModalOpen(false);
            loadRows();
        } catch (e) {
            message.error(e?.response?.data?.detail || 'Kayıt sırasında hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (record) => {
        try {
            await axios.delete(`${API_BASE_URL}/depo-giris/${record.id}`, { headers: authHeaders() });
            message.success('Kayıt silindi');
            loadRows();
        } catch (e) {
            message.error(e?.response?.data?.detail || 'Silme sırasında hata oluştu');
        }
    };

    // ---- Hakediş ----
    const openHakedis = (record) => {
        setHakedisRecord(record);
        hakedisForm.resetFields();
        hakedisForm.setFieldsValue({ kdv_orani: 20, kdv_dahil: 0 });
        setHakedisOpen(true);
        if (!finLookups) {
            loadFinanceData();
        }
    };

    const hakedisSummary = useMemo(() => {
        if (!hakedisRecord) return null;
        const ara = Number(hakedisRecord.miktar || 0) * Number(hakedisRecord.birim_fiyat || 0);
        const k = Number(wKdvOrani ?? 20) / 100;
        let matrah;
        let kdv;
        if (Number(wKdvDahil) === 1) {
            matrah = ara / (1 + k);
            kdv = ara - matrah;
        } else {
            matrah = ara;
            kdv = matrah * k;
        }
        const oran = Number((tevkifatlar || []).find((t) => t.kod === wTevkifat)?.oran || 0);
        const tevkifat = kdv * oran;
        const odenecek = matrah + kdv - tevkifat;
        return { ara, matrah, kdv, tevkifat, odenecek };
    }, [hakedisRecord, wKdvOrani, wKdvDahil, wTevkifat, tevkifatlar]);

    const handleHakedisCreate = async () => {
        let values;
        try {
            values = await hakedisForm.validateFields();
        } catch {
            return;
        }
        const body = {
            cari_id: values.cari_id,
            firma_id: values.firma_id,
            kdv_orani: values.kdv_orani ?? 20,
            kdv_dahil: values.kdv_dahil ?? 0,
        };
        if (values.tevkifat_kodu) {
            body.tevkifat_kodu = values.tevkifat_kodu;
        }
        setHakedisSaving(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/depo-giris/${hakedisRecord.id}/hakedis`,
                body,
                { headers: authHeaders() }
            );
            const hid = res?.data?.data?.id ?? res?.data?.id;
            message.success(`Hakediş oluşturuldu${hid ? ` (#${hid})` : ''}`);
            setHakedisOpen(false);
            setHakedisRecord(null);
            loadRows();
        } catch (e) {
            message.error(e?.response?.data?.detail || 'Hakediş oluşturulamadı');
        } finally {
            setHakedisSaving(false);
        }
    };

    // ---- Table ----
    const columns = [
        {
            title: 'Tarih',
            dataIndex: 'tarih',
            key: 'tarih',
            render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : ''),
        },
        { title: 'Depo', dataIndex: 'depo_ad', key: 'depo_ad' },
        { title: 'Üretici', dataIndex: 'uretici_ad', key: 'uretici_ad' },
        { title: 'QR', dataIndex: 'qr', key: 'qr' },
        { title: 'Kabin Tipi', dataIndex: 'kabin_tipi', key: 'kabin_tipi' },
        { title: 'Miktar', dataIndex: 'miktar', key: 'miktar', align: 'right' },
        {
            title: 'Birim Fiyat',
            dataIndex: 'birim_fiyat',
            key: 'birim_fiyat',
            align: 'right',
            render: (v) => fmt(v),
        },
        {
            title: 'Toplam',
            key: 'toplam',
            align: 'right',
            render: (_, record) => fmt(Number(record.miktar || 0) * Number(record.birim_fiyat || 0)),
        },
        {
            title: 'Durum',
            dataIndex: 'durum',
            key: 'durum',
            render: (v, record) =>
                Number(v) === 2 ? (
                    <Tag color="green">{`Hakediş #${record.hakedis_id}`}</Tag>
                ) : (
                    <Tag color="blue">Girildi</Tag>
                ),
        },
        {
            title: 'İşlem',
            key: 'islem',
            render: (_, record) => {
                if (Number(record.durum) !== 1) return null;
                return (
                    <Space size="small">
                        <Button size="small" onClick={() => openEdit(record)}>
                            Düzenle
                        </Button>
                        <Popconfirm
                            title="Bu kayıt silinsin mi?"
                            okText="Sil"
                            cancelText="Vazgeç"
                            onConfirm={() => handleDelete(record)}
                        >
                            <Button size="small" danger>
                                Sil
                            </Button>
                        </Popconfirm>
                        <Button size="small" type="primary" onClick={() => openHakedis(record)}>
                            Hakediş Oluştur
                        </Button>
                    </Space>
                );
            },
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 12 }} wrap>
                <Button icon={<ReloadOutlined />} onClick={loadRows} loading={loading}>
                    Yenile
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    Yeni Giriş
                </Button>
                <Text type="secondary">{rows.length} kayıt</Text>
            </Space>

            <Table
                size="small"
                rowKey="id"
                columns={columns}
                dataSource={rows}
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={{
                    pageSize: 50,
                    showTotal: (total) => `Toplam ${total} kayıt`,
                }}
            />

            {/* Create / Edit Modal */}
            <Modal
                title={editing ? 'Kabin Girişi Düzenle' : 'Yeni Kabin Girişi'}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                okText="Kaydet"
                cancelText="Vazgeç"
                confirmLoading={saving}
                forceRender
                afterOpenChange={(open) => {
                    if (open && qrInputRef.current) {
                        qrInputRef.current.focus();
                    }
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="tarih"
                        label="Tarih"
                        rules={[{ required: true, message: 'Tarih seçiniz' }]}
                    >
                        <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="depo_lokasyon"
                        label="Depo"
                        rules={[{ required: true, message: 'Depo seçiniz' }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Depo seçin"
                            options={depoOptions}
                        />
                    </Form.Item>
                    <Form.Item
                        name="uretici"
                        label="Üretici"
                        rules={[{ required: true, message: 'Üretici seçiniz' }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Üretici seçin"
                            options={ureticiOptions}
                        />
                    </Form.Item>
                    <Form.Item
                        name="qr"
                        label="QR / Platform No (aralık girilebilir)"
                        tooltip="Kabinlerin alındığı QR/Platform numaraları. Aralık için tire: 20-25 (aradakiler dahil). Birden çok: 12,13,20-25"
                        extra={qrCount > 0 ? `${qrCount} kabin (miktar otomatik)` : 'Örn: 12,13,14 veya 20-25'}
                    >
                        <Input.TextArea
                            ref={qrInputRef}
                            autoSize={{ minRows: 1, maxRows: 3 }}
                            placeholder="Örn: 12,13,14  veya  20-25  (aradakiler dahil)"
                        />
                    </Form.Item>
                    <Form.Item name="kabin_tipi" label="Kabin Tipi">
                        <Input placeholder="Kabin tipi" />
                    </Form.Item>
                    <Form.Item
                        name="miktar"
                        label="Miktar (QR adedinden otomatik)"
                        rules={[{ required: true, message: 'Miktar giriniz' }]}
                    >
                        <InputNumber min={1} style={{ width: '100%' }} disabled={qrCount > 0} />
                    </Form.Item>
                    <Form.Item
                        name="birim_fiyat"
                        label="Birim Fiyat"
                        rules={[{ required: true, message: 'Birim fiyat giriniz' }]}
                    >
                        <InputNumber {...inputNumberMoneyProps} min={0} step={0.01} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="aciklama" label="Açıklama">
                        <Input.TextArea rows={2} placeholder="Açıklama" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Hakediş Modal */}
            <Modal
                title="Hakediş Oluştur"
                open={hakedisOpen}
                onOk={handleHakedisCreate}
                onCancel={() => setHakedisOpen(false)}
                okText="Hakediş Oluştur"
                cancelText="Vazgeç"
                confirmLoading={hakedisSaving}
                forceRender
            >
                {hakedisRecord && (
                    <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
                        <Descriptions.Item label="Üretici">
                            {hakedisRecord.uretici_ad || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ara Toplam">
                            {`${hakedisRecord.miktar} × ${fmt(hakedisRecord.birim_fiyat)} = ${fmt(
                                Number(hakedisRecord.miktar || 0) * Number(hakedisRecord.birim_fiyat || 0)
                            )}`}
                        </Descriptions.Item>
                    </Descriptions>
                )}
                <Form form={hakedisForm} layout="vertical">
                    <Form.Item
                        name="cari_id"
                        label="Cari"
                        rules={[{ required: true, message: 'Cari seçiniz' }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Cari seçin"
                            options={cariOptions}
                        />
                    </Form.Item>
                    <Form.Item
                        name="firma_id"
                        label="Firma"
                        rules={[{ required: true, message: 'Firma seçiniz' }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Firma seçin"
                            options={firmaOptions}
                        />
                    </Form.Item>
                    <Form.Item name="kdv_orani" label="KDV %">
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="kdv_dahil" label="KDV">
                        <Radio.Group>
                            <Radio value={0}>KDV Hariç</Radio>
                            <Radio value={1}>KDV Dahil</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item name="tevkifat_kodu" label="Tevkifat">
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Tevkifat (opsiyonel)"
                            options={tevkifatOptions}
                        />
                    </Form.Item>
                </Form>
                {hakedisSummary && (
                    <>
                        <Divider style={{ margin: '12px 0' }} />
                        <Descriptions size="small" column={2}>
                            <Descriptions.Item label="Matrah">{fmt(hakedisSummary.matrah)}</Descriptions.Item>
                            <Descriptions.Item label="KDV">{fmt(hakedisSummary.kdv)}</Descriptions.Item>
                            <Descriptions.Item label="Tevkifat">
                                {fmt(hakedisSummary.tevkifat)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ödenecek">
                                <Text strong>{fmt(hakedisSummary.odenecek)}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default DepoKabinGirisiTab;
